import type { ReactNode } from "react";

import { IgSegmentedControl } from "@intelligems/ig-theme";
import { Box, Stack } from "@mui/material";
import { useImmer } from "use-immer";

import { useAgenticLogicRuleVersionDetail } from "@src/api/agentic-logic/get-agentic-logic-rule-version-detail";

import { SelectionLogicTab } from "../graph/SelectionLogicTab";

/** The canvas needs a real height; a card expander has none of its own. */
const GRAPH_HEIGHT = 420;

type VersionBodyView = "graph" | "source";

/**
 * One published version's selection-logic graph, read from the version-detail
 * endpoint that already serves the page's canvas.
 *
 * No new request shape and no new endpoint: `useAgenticLogicRuleVersionDetail`
 * has always been parameterized by an arbitrary version number, and its
 * `detail.graph` is the same `RuleGraphDto` the live canvas consumes. Until
 * now only the draft and the active version were ever asked for.
 *
 * The read is deliberately inside the expanded card rather than at the panel:
 * a rule with twenty versions would otherwise fire twenty detail reads to draw
 * a list of twenty one-line rows.
 */
const RuleVersionGraph = ({
  ruleId,
  version,
}: {
  ruleId: string;
  version: number;
}) => {
  const detailQuery = useAgenticLogicRuleVersionDetail(ruleId, version);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: GRAPH_HEIGHT,
        minHeight: 0,
      }}
    >
      <SelectionLogicTab
        graph={detailQuery.data?.detail.graph ?? null}
        isLoading={detailQuery.isLoading}
        // A version published before the rule ever had a graph artifact is a
        // real, permanent state — not something the merchant can fix by
        // saving, which is what the tab's default empty copy would tell them.
        emptyHint={`Version ${version} was published without a selection-logic graph.`}
      />
    </Box>
  );
};

/**
 * The body of an expanded version card.
 *
 * The graph is the default and, for most orgs, the whole of it: a version card
 * exists to answer "what did this version DO", and a page of TypeScript (or a
 * blob of template JSON) answers that only for the people who wrote it. The
 * source stays reachable for orgs with `advanced_agentic_logic_enabled` —
 * withholding it from the people who author rules in code would be a
 * regression — but it is behind a toggle rather than in front of the graph.
 */
export const RuleVersionBody = ({
  ruleId,
  version,
  source,
  sourceLabel,
}: {
  ruleId: string;
  version: number;
  /**
   * The version's raw source view, or `undefined` when the org may not see it.
   * `undefined` also drops the toggle entirely: a two-option control with one
   * option is a control that does nothing.
   */
  source?: ReactNode;
  /** What the source view is called for this rule flavour ("Code", "Configuration"). */
  sourceLabel: string;
}) => {
  const [view, setView] = useImmer<VersionBodyView>("graph");
  const showSource = source !== undefined && view === "source";

  return (
    <Stack spacing={1.5}>
      {source !== undefined ? (
        <IgSegmentedControl<VersionBodyView>
          ariaLabel={`Version ${version} view`}
          options={[
            { value: "graph", label: "Graph" },
            { value: "source", label: sourceLabel },
          ]}
          sx={{ alignSelf: "flex-start", mx: 2 }}
          value={view}
          allowDeselect={false}
          onChange={(next) => {
            if (next !== null) setView(() => next);
          }}
        />
      ) : null}
      {showSource ? (
        source
      ) : (
        <RuleVersionGraph ruleId={ruleId} version={version} />
      )}
    </Stack>
  );
};
